import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { BentoComboboxOptions } from '@bento/bento-ng';
import { ProfileReportsService } from '../../services/profile-reports.service';
import { BehaviorSubject } from 'rxjs';
import { getEmployeeViews} from '@onviobr/services/core/employee-views';
import { NgbDateNativeAdapter, NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { ReportProfileSelected } from '../../models/report';
import { BfmGroupEvent } from '@bento/bfm-ng';
import { ReportProfileBase } from '../base-report/firm-managment-report.base';
import { CustomDateParserFormatter } from '../../ngb-date-parse-formatter-custom/ngb-date-parse-formatter-custom';

@Component({
	selector: 'app-firm-management-report-option',
	templateUrl: './firm-management-report-option.component.html',
	styleUrls: ['./firm-management-report-option.component.css'],
	// https://ng-bootstrap.github.io/#/components/datepicker/examples#adapter
	// by default it takes in object format
	providers: [{ provide: NgbDateAdapter, useClass: NgbDateNativeAdapter },
		{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
})
export class FirmManagementReportOptionComponent extends ReportProfileBase implements OnInit {

	set selectedProfile(profile: ReportProfileSelected) {
		this.selectedProfileInfo = profile;
		this.profileId = this.selectedProfile.id;
		if (this.companyId) {
			this.readProfileDetail();
		}
	}
	@Input() get selectedProfile() {
		return this.selectedProfileInfo;
	}
	selectedProfileInfo: ReportProfileSelected;

	validations = {
		required: '{0} is required',
		name: {
			// eslint-disable-next-line @typescript-eslint/naming-convention
			ReportProfileNameMustBeUnique: '{0} already exists',
		},
	};
	getEmployeeViews = getEmployeeViews;
	comboboxOptions: BentoComboboxOptions = {
		searchable: true,
		searchCompare: (row, search) => {
			const searchLowerCase = search.toLocaleLowerCase();
			const name = `${row.name}`.toLowerCase();

			return name.indexOf(searchLowerCase) > -1 || row.id.toLocaleLowerCase().indexOf(searchLowerCase) > -1;
		},
		labelFormatter: row => row.name
	};
	datesData: BehaviorSubject<any[]>;

	constructor(public fb: FormBuilder, public profileReportsService: ProfileReportsService) {
		super(fb, profileReportsService);
		this.formControlInit();
	}

	async ngOnInit() {
		const bindings = await this.getBindings();
		this.companyId = bindings.ApplicationConfiguration.CompanyId;
		this.contactId = bindings.ApplicationConfiguration.ContactId;
		this.readEmployeeId();
		this.profileReportsService.readReportDateRange().subscribe(data => {
			const rows = data.items.map(x => ({ id: x.id, name: x.name }));
			this.datesData = new BehaviorSubject(rows);
		});

		this.readProfileDetail();
		this.handleReportDateRange();
		this.handleDateRange();
	}

	formControlInit() {
		// set reactive form control
		this.profileForm = this.fb.group({
			id: [null],
			name: [null, [Validators.required]],
			useProfileDateRange: [null, [Validators.required]],
			reportDateRange: [null, [Validators.required]],
			multidate: this.fb.group({
				startDate: [null],
				endDate: [null],
			}),
			profileType: [null]// used to store Firm/Staff
		});
	}

	profileSubmit(): void {
		this.alerts = [];
		if (!this.isSubmitAllowed) {
			this.showAlerts();
			this.markFormGroupTouched(this.profileForm);
		} else {
			this.saveProfile();
		}
	}

	formToDto(): any {
		const formData = this.profileForm.getRawValue();
		this.mapDate(formData, this.profileDetail);
		Object.assign(this.profileDetail, formData);

		this.profileDetail.startDate = formData.multidate.startDate;
		this.profileDetail.endDate = formData.multidate.endDate;

		return this.profileDetail;
	}

	saveProfile(): void {
		this.isLoaderBusy = true;
		const model = this.formToDto();
		this.profileReportsService.updateProfile(model)
			.subscribe(() => {
				this.saveProfileSuccess();
			},
			(error) => this.saveProfileError(error),
			() => this.isLoaderBusy = false);
	}

	saveProfileSuccess(): void {
		this.readProfileDetail();
		this.refreshSingleProfileTree();
	}

	setValueBeforePatch() {
		this.profileForm.get('profileType').setValue(this.selectedProfileInfo.profileType);
	}

	setValueAfterPatch() {
		this.setStartAndEndDate();
	}

	cancel() {
		this.profileForm.patchValue(this.profileDetail);
		this.setStartAndEndDate();
	}

	setStartAndEndDate() {
		if (!this.profileDetail.startDate) {
			return;
		}
		this.setControlValue('multidate.startDate', new Date(this.profileDetail.startDate));
		this.setControlValue('multidate.endDate', new Date(this.profileDetail.endDate));
	}

	handleReportDateRange() {
		this.profileForm.get('useProfileDateRange').valueChanges.subscribe(x => {
			this.profileForm.get('reportDateRange').patchValue(null, {onlySelf: true});

			this.profileForm.get('reportDateRange').clearValidators();
			this.profileForm.get('reportDateRange').updateValueAndValidity();

			this.clearValueAndValidationForStartAndEndDate();
			if (x) {
				this.enableControl('reportDateRange');
			} else {
				this.disableControl('reportDateRange');
			}
		});
	}

	handleDateRange() {
		this.profileForm.get('reportDateRange').valueChanges.subscribe(x => {
			if (!x) {
				return null;
			}
			this.clearValueAndValidationForStartAndEndDate();
			if (x.id === 'CUSTOM') {
				this.enableControl('multidate.startDate');
				this.enableControl('multidate.endDate');
				this.profileForm.get('multidate.startDate').setValidators(Validators.required);
				this.profileForm.get('multidate.endDate').setValidators(Validators.required);
				this.profileForm.get('multidate.startDate').updateValueAndValidity();
				this.profileForm.get('multidate.endDate').updateValueAndValidity();
			} else if (this.profileForm.get('useProfileDateRange').value === true ) {
				this.enableControl('reportDateRange');
				this.profileForm.get('reportDateRange').setValidators(Validators.required);
				this.disableControl('multidate.startDate');
				this.disableControl('multidate.endDate');
				this.profileForm.updateValueAndValidity();
			} else {
				this.disableControl('multidate.startDate');
				this.disableControl('multidate.endDate');
			}
		});
	}

	clearValueAndValidationForStartAndEndDate() {
		const daterange = this.profileForm.get('multidate');
		if (daterange.get('startDate').value) {
			daterange.get('startDate').setValue(null);
			daterange.get('startDate').clearValidators();
			daterange.get('startDate').updateValueAndValidity();
		}
		if (daterange.get('endDate').value) {
			daterange.get('endDate').setValue(null);
			daterange.get('endDate').clearValidators();
			daterange.get('endDate').updateValueAndValidity();
		}
	}

	validateSelectedDates(event: BfmGroupEvent): void {
		if (!event) {
			return;
		}

		const startDate = event.formGroup.get('startDate').value;
		const endDate = event.formGroup.get('endDate').value;

		if (!startDate || !endDate) {
			return event.setValidity({ valid: false, message: 'From Date and To Date are required' });
		}

		if (startDate && endDate && (startDate < endDate)) {
			return event.setValidity({ valid: true });
		} else {
			return event.setValidity({ valid: false, message: 'From date must be before To Date' });
		}
	}
}
